/**
 * requestAnimationFrame tween helpers: move glides (ease-out cubic, ~180 ms)
 * and scalar effects (capture pop). Pure timing logic; the caller re-renders
 * the board on each frame.
 */

export interface Point {
  x: number;
  y: number;
}

export interface AnimationOptions {
  durationMs: number;
  onFrame: (center: Point) => void;
  onDone: () => void;
}

export interface TickOptions {
  durationMs: number;
  onFrame: (t: number) => void;
  onDone: () => void;
}

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function interpolate(from: Point, to: Point, t: number): Point {
  return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
}

/** Raw eased-time driver. Returns a cancel function (cancel == no onDone). */
export function animateTicks(opts: TickOptions): () => void {
  let raf = 0;
  let start: number | null = null;
  const step = (now: number) => {
    if (start === null) start = now;
    const t = Math.min(1, (now - start) / opts.durationMs);
    opts.onFrame(t);
    if (t < 1) {
      raf = requestAnimationFrame(step);
    } else {
      opts.onDone();
    }
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

/** Animate a point from→to with ease-out cubic. Returns a cancel function. */
export function animatePoint(from: Point, to: Point, opts: AnimationOptions): () => void {
  return animateTicks({
    durationMs: opts.durationMs,
    onFrame: (t) => opts.onFrame(interpolate(from, to, easeOutCubic(t))),
    onDone: opts.onDone,
  });
}
